import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View, Text, StyleSheet, ScrollView } from 'react-native'
import { Header } from './Header'
import { DisconnectedList } from './DisconnectedList'
import {
  PlaidConnectButton,
  PlaidConnectButtonRef,
} from '../../../../ui/add-payment-method-components/PlaidConnectButton'
import { BtButton } from '@linqpal/components/src/ui'
import RootStore from '../../../../store'
import { BankConnectionType } from '@linqpal/models/src/dictionaries'
import { BankAccountItem } from '../../../GeneralApplication/Application/flow/bank/BankAccountList'
import { observer } from 'mobx-react'
import { BankAccount, IUIBankAccount, routes } from '@linqpal/models'
import { toJS } from 'mobx'
import { Spacer } from '../../../../ui/atoms'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import { useResponsive } from '@linqpal/components/src/hooks'
import Modal from '../../../../ui/atoms/Modal'
import InvoiceActionsStore from '../../InvoicesTab/InvoiceActionsStore'
import { isWeb } from '../../../../utils/helpers/commonUtils'
import cookies from '../../../../utils/service/cooker'

interface ConnectBankProps {
  recheckConditionsBeforeApplyingCredit: () => void
}

export const ConnectBank: React.FC<ConnectBankProps> = observer(
  ({ recheckConditionsBeforeApplyingCredit }) => {
    const { t } = useTranslation('global')
    const { sm } = useResponsive()
    const { reconnectCreditBank, setReconnectCreditBank } = InvoiceActionsStore
    const { onlineConnectedBanks } = RootStore.screensStore.paymentMethodsStore
    const draft = RootStore.userStore.document
    const ref = useRef<null | PlaidConnectButtonRef>(null)
    const [loading, setLoading] = useState(false)
    const [selected, setSelected] = useState<null | IUIBankAccount>(null)
    const [loadingBankId, setLoadingBankId] = useState<string>('')
    const plaidBanks: IUIBankAccount[] = onlineConnectedBanks.filter(
      (b) => b.connectionType === BankConnectionType.PLAID,
    )
    const canContinue = useMemo(
      () => selected && selected.connectionType === BankConnectionType.PLAID,
      [selected],
    )
    const isUserAuthenticated = RootStore.userStore.isAuthenticated
    const session = cookies.get('session')
    const authenticated = (isWeb && session) || isUserAuthenticated

    useEffect(() => {
      authenticated && RootStore.userStore.fetchDocument('loan_application')
    }, [authenticated])

    const onContinue = () => {
      if (selected && draft) {
        const bank = toJS(
          BankAccount.create({ ...toJS(selected), isPrimaryForCredit: true }),
        )
        routes.company.addBankAccount(bank).then(async () => {
          draft.setData('details', bank, '', 'bank', 'BankDetails', true)
          await RootStore.screensStore.paymentMethodsStore.fetchPaymentMethods()
          setReconnectCreditBank(false)
          recheckConditionsBeforeApplyingCredit()
        })
      }
    }

    const connectBankButton = (
      <BtButton
        onPress={() => ref.current?.createToken()}
        loading={loading && !loadingBankId}
        appearance="outline"
        disabled={RootStore.isBusy}
      >
        {t('ConnectBankWithPlaid.connect-bank-account')}
      </BtButton>
    )

    const onSelectBankAccount = (bank) => {
      if (bank.isExpiredPlaidBank) {
        setLoadingBankId(bank._id)
        ref.current?.createToken(bank._id)
      } else if (bank.isDisconnectedPlaidBank) {
        ref.current?.createToken('', bank.routingNumber)
      } else setSelected(bank)
    }

    if (!reconnectCreditBank) return null

    return (
      <Modal
        style={{
          flex: 1,
          margin: 0,
          backgroundColor: 'white',
        }}
        visible={reconnectCreditBank}
        onClose={() => {}}
        animationIn="zoomIn"
        coverScreen={true}
      >
        <Header />
        <ScrollView
          style={composeStyle(
            { alignSelf: 'center' },
            sm ? { maxWidth: 670 } : { width: '100%', padding: 20 },
          )}
        >
          <Text style={styles.message1Desktop}>
            {t('ConnectBankWithPlaid.reconnect-all-banks')}
          </Text>
          <DisconnectedList />
          {plaidBanks?.length ? (
            <>
              <Text
                style={{
                  fontWeight: '600',
                  fontSize: sm ? 24 : 20,
                  lineHeight: sm ? 36 : 30,
                  marginTop: 40,
                }}
              >
                {t('ConnectBankWithPlaid.banks-connected')}
              </Text>
              <Text
                style={composeStyle(styles.connectedText, {
                  fontSize: sm ? 18 : 16,
                })}
              >
                {t('ConnectBankWithPlaid.connected-accounts-shown-below')}
              </Text>
              <Text
                style={composeStyle(styles.selectPrimaryText, {
                  fontSize: sm ? 18 : 16,
                })}
              >
                {t('ConnectBankWithPlaid.select-primary-for-loan-payment')}
              </Text>
              <View style={{ flexDirection: sm ? 'row' : 'column' }}>
                {plaidBanks.map((b) => {
                  return (
                    <View style={{ flexDirection: 'column' }}>
                      <BankAccountItem
                        bankAccount={b}
                        selectedBankAccountId={selected?._id ?? ''}
                        onPress={onSelectBankAccount}
                        loadingBankId={loadingBankId}
                        showDisconnectedBadge
                      />
                      {selected?._id === b.id && (
                        <Text
                          style={{
                            fontWeight: '400',
                            fontSize: 12,
                            color: '#668598',
                            marginTop: 10,
                          }}
                        >
                          {t('ConnectBankWithPlaid.selected-as-primary')}
                        </Text>
                      )}
                      {!sm && <Spacer height={20} />}
                    </View>
                  )
                })}
              </View>
              <View
                style={{
                  height: 1,
                  borderRadius: 2,
                  width: '100%',
                  backgroundColor: '#CCD6DD',
                  marginTop: sm ? 30 : 15,
                }}
              />
              <Text
                style={{
                  fontWeight: '600',
                  fontSize: sm ? 24 : 20,
                  lineHeight: 36,
                  marginTop: sm ? 20 : 10,
                }}
              >
                {t('ConnectBankWithPlaid.or-connect-new-bank')}
              </Text>
              <Spacer height={sm ? 30 : 25} />
              {connectBankButton}
            </>
          ) : (
            <>
              <Text
                style={composeStyle(
                  styles.message2,
                  !sm && styles.mobileMessage2,
                )}
              >
                {t('ConnectBankWithPlaid.connect-accounts')}
              </Text>
              <Text
                style={composeStyle(
                  styles.message3,
                  !sm && styles.mobileMessage3,
                )}
              >
                {t('ConnectBankWithPlaid.to-review-business-cashflow')}
              </Text>
              {connectBankButton}
            </>
          )}
          <PlaidConnectButton
            ref={ref}
            setLoading={setLoading}
            onConnectSuccess={() => {
              if (loadingBankId) setLoadingBankId('')
            }}
          />
          <BtButton
            style={{ width: 133, alignSelf: 'flex-end', marginTop: 30 }}
            onPress={onContinue}
            disabled={!canContinue || RootStore.isBusy}
          >
            {t('ConnectBankWithPlaid.continue-button')}
          </BtButton>
        </ScrollView>
      </Modal>
    )
  },
)

const styles = StyleSheet.create({
  message1Desktop: {
    fontWeight: '700',
    fontSize: 18,
    lineHeight: 28,
    alignSelf: 'center',
    marginTop: 25,
    textAlign: 'center',
  },
  message2: {
    fontWeight: '600',
    fontSize: 24,
    marginTop: 40,
    lineHeight: 48,
  },
  mobileMessage2: {
    fontSize: 20,
    lineHeight: 30,
  },
  message3: {
    fontWeight: '500',
    fontSize: 18,
    color: '#626D75',
    lineHeight: 30,
    marginBottom: 30,
  },
  mobileMessage3: {
    fontSize: 16,
    lineHeight: 28,
  },
  selectPrimaryText: {
    fontWeight: '600',
    fontSize: 18,
    marginBottom: 35,
  },
  connectedText: {
    fontWeight: '500',
    fontSize: 18,
    lineHeight: 30,
    color: '#626D75',
    marginBottom: 20,
  },
})
